/**
 * Extensions page — Skills / Gadget / Apps / External apps tabs.
 * Gadget: registry_entries kind=gadget (seeded builtins + per-user imports).
 * Footer Open / kebab Open / rail Pin: full-viewport. Edit in a chat docks beside Ros.
 * Apps: registry kind=app (hidden filtered). Open renders full-viewport at /apps/:id.
 * External apps: kind=tool | external_app (Option A). Launch opens resolved URL in a new tab.
 * Skills tab: former Skills page, embedded here.
 *
 * Share opens a user picker (OIDC /dev/users) and sends share_blueprint invites.
 * Apps also get Add Users (add_member invites). Recipients Accept/Reject in the rail notif center.
 * Pin is local-only (shared with the rail Pinned section). Delete uses DELETE registry_entries.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ComponentType } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import Snackbar from '@mui/material/Snackbar'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import { alpha, useTheme } from '@mui/material/styles'
import SearchIcon from '@mui/icons-material/Search'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import ViewSidebarIcon from '@mui/icons-material/ViewSidebar'
import ForumIcon from '@mui/icons-material/Forum'
import ShareIcon from '@mui/icons-material/Share'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import PushPinIcon from '@mui/icons-material/PushPin'
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined'
import DeleteIcon from '@mui/icons-material/Delete'
import UploadIcon from '@mui/icons-material/Upload'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize'
import GroupsIcon from '@mui/icons-material/Groups'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useRosShell } from '../RosShellContext'
import { useRosFeatures } from '../access/useRosFeatures'
import { SERIF_HEADING } from '../theme'
import { ds } from '../components/agent-builder/theme'
import { ExtensionCard } from '../components/ExtensionCard'
import type { ExtensionCardMenuItem } from '../components/ExtensionCard'
import { ShareUsersDialog } from '../components/ShareUsersDialog'
import { SkillsPage } from './SkillsPage'
import { listRegistryEntries, deleteRegistryEntry, type RegistryEntry, type RegistryKind } from './registryApi'
import type { InviteType } from './extensionInvitesApi'
import {
  enrichPinnedFromEntries,
  gadgetDefinitionId,
  isCapnWebExtension,
  pinnedFromRegistryEntry,
  resolveExtensionUrl,
  togglePinnedExtension,
  usePinnedIdSet,
} from '../pinnedExtensions'

type ExtensionsTab = 'gadget' | 'apps' | 'external' | 'skills'

const REGISTRY_TAB_KINDS: Record<'gadget' | 'apps' | 'external', RegistryKind[]> = {
  gadget: ['gadget'],
  apps: ['app'],
  external: ['tool', 'external_app'],
}

type PaletteColorKey = 'primary' | 'info' | 'success' | 'warning'

const TAB_META: Record<ExtensionsTab, {
  label: string
  icon: ComponentType<{ fontSize?: 'small' }>
  colorKey: PaletteColorKey
  help: string
  actionLabel: string
  actionIcon: ComponentType<{ fontSize?: 'small' }>
}> = {
  skills: {
    label: 'Skills',
    icon: AutoAwesomeIcon,
    colorKey: 'primary',
    help: "Instructions and workflows Ros follows inside a chat. Turn one on and Ros uses it when the conversation calls for it.",
    actionLabel: 'Open',
    actionIcon: ViewSidebarIcon,
  },
  gadget: {
    label: 'Gadgets',
    icon: DashboardCustomizeIcon,
    colorKey: 'info',
    help: 'Small applications that run in the preview panel. A gadget is yours alone and keeps its state between sessions.',
    actionLabel: 'Open',
    actionIcon: ViewSidebarIcon,
  },
  apps: {
    label: 'Apps',
    icon: GroupsIcon,
    colorKey: 'success',
    help: 'Shared applications with one global state, so nothing to set up.',
    actionLabel: 'Open',
    actionIcon: ViewSidebarIcon,
  },
  external: {
    label: 'External apps',
    icon: OpenInNewIcon,
    colorKey: 'warning',
    help: 'Set up and approved by an administrator. An external app can also send artifacts back into Ros.',
    actionLabel: 'Launch',
    actionIcon: OpenInNewIcon,
  },
}

const TAB_ORDER: ExtensionsTab[] = ['skills', 'gadget', 'apps', 'external']

export function ExtensionsPage() {
  const shell = useRosShell()
  const { enabled } = useRosFeatures()
  const navigate = useNavigate()
  const location = useLocation()
  const { definitionId } = useParams()
  const theme = useTheme()
  const showSkills = enabled('ros-skills')
  const navState = (location.state || {}) as {
    extensionsTab?: ExtensionsTab
    extensionsRefresh?: number
  }
  const focusTab = navState.extensionsTab
  const refreshKey = navState.extensionsRefresh
  const [tab, setTab] = useState<ExtensionsTab>(
    focusTab === 'gadget' || focusTab === 'apps' || focusTab === 'external' || focusTab === 'skills'
      ? focusTab
      : 'gadget',
  )
  const [listEpoch, setListEpoch] = useState(0)
  const [search, setSearch] = useState('')
  const [entries, setEntries] = useState<RegistryEntry[]>([])
  const [error, setError] = useState<string | null>(null)
  const [entriesLoading, setEntriesLoading] = useState(true)
  const [snackbar, setSnackbar] = useState<string | null>(null)
  const pinnedIds = usePinnedIdSet()
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [inviteTarget, setInviteTarget] = useState<{
    type: InviteType
    definitionId: string
    displayName: string
  } | null>(null)
  const [importing, setImporting] = useState(false)
  const importInputRef = useRef<HTMLInputElement>(null)
  const loading = tab !== 'skills' && entriesLoading

  const visibleTabs = useMemo(() => TAB_ORDER.filter(t => t !== 'skills' || showSkills), [showSkills])

  const authHeaders = useCallback((contentType?: string): Record<string, string> => {
    const headers: Record<string, string> = {}
    if (shell.token) headers.Authorization = `Bearer ${shell.token}`
    if (contentType) headers['Content-Type'] = contentType
    return headers
  }, [shell.token])

  /** Library Import: raw zip → POST /api/micro-apps/import (same as former Micro-apps page). */
  const handleImportPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setImporting(true)
    setError(null)
    try {
      const res = await fetch(`${shell.apiBaseUrl}/api/micro-apps/import`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders('application/octet-stream'),
        body: file,
      })
      const body = await res.json().catch(() => ({})) as {
        error?: string
        details?: string[]
        id?: string
        displayName?: string
        isolation?: string
      }
      if (!res.ok) {
        const detail = Array.isArray(body.details) && body.details.length
          ? body.details.join('; ')
          : body.error || `Import failed (${res.status})`
        throw new Error(detail)
      }
      const definitionId = body.id
      if (!definitionId) throw new Error('Import succeeded but returned no id')
      const displayName = body.displayName || definitionId
      const isolation = body.isolation === 'shared' ? 'shared' : 'per-user'
      setTab(isolation === 'shared' ? 'apps' : 'gadget')
      setListEpoch((n) => n + 1)
      setSnackbar(`Imported "${displayName}". Opening…`)
      navigate(`/gadgets/${definitionId}`, {
        state: {
          displayName,
          kind: isolation === 'shared' ? 'app' : 'gadget',
        },
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setImporting(false)
    }
  }

  // After Accept, the rail navigates here with state. Apply tab + bump epoch in one update so the
  // catalog refetch uses the right tab even when /extensions was already mounted.
  useEffect(() => {
    if (refreshKey == null) return
    if (focusTab === 'gadget' || focusTab === 'apps' || focusTab === 'external' || focusTab === 'skills') {
      setTab(focusTab)
    }
    setListEpoch(refreshKey)
  }, [refreshKey, focusTab])

  const togglePin = (entry: RegistryEntry) => {
    if (tab !== 'gadget' && tab !== 'apps' && tab !== 'external') return
    togglePinnedExtension(pinnedFromRegistryEntry(entry, tab))
  }

  const openShare = (definitionId: string, displayName: string) => {
    setInviteTarget({ type: 'share_blueprint', definitionId, displayName })
  }

  const openAddUsers = (definitionId: string, displayName: string) => {
    setInviteTarget({ type: 'add_member', definitionId, displayName })
  }

  const editInChat = (name: string) => {
    shell.startNewChatWithMessage(`Help me edit the ${name} ${tab === 'gadget' ? 'gadget' : 'app'}.`)
  }

  const handleDeleteEntry = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await deleteRegistryEntry(shell.apiBaseUrl, shell.token, deleteTarget.id)
      setEntries(prev => prev.filter(e => e.id !== deleteTarget.id))
      setSnackbar(`Deleted "${deleteTarget.name}"`)
      setDeleteTarget(null)
    } catch (err) {
      setSnackbar(err instanceof Error ? err.message : 'Failed to delete')
    } finally {
      setDeleting(false)
    }
  }

  useEffect(() => {
    if (tab !== 'apps' && tab !== 'external' && tab !== 'gadget') return
    let cancelled = false
    ;(async () => {
      setEntriesLoading(true)
      setError(null)
      try {
        const data = await listRegistryEntries(shell.apiBaseUrl, shell.token, REGISTRY_TAB_KINDS[tab])
        const visible = data.filter((e) => !e.hidden)
        if (!cancelled) {
          setEntries(visible)
          if (tab === 'gadget' || tab === 'apps' || tab === 'external') {
            enrichPinnedFromEntries(visible, tab)
          }
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err))
      } finally {
        if (!cancelled) setEntriesLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [tab, shell.apiBaseUrl, shell.token, listEpoch])

  useEffect(() => {
    if (!definitionId) return
    // Deep link /extensions/:id → full-viewport Open (not edit-beside-chat).
    navigate(`/gadgets/${definitionId}`, { replace: true, state: { displayName: definitionId } })
  }, [definitionId, navigate])

  const openGadgetBesideChat = (entry: RegistryEntry) => {
    shell.openMicroApp(gadgetDefinitionId(entry), entry.name)
  }

  const openGadgetFullPage = (entry: RegistryEntry) => {
    const defId = gadgetDefinitionId(entry)
    navigate(`/gadgets/${defId}`, {
      state: {
        displayName: entry.name,
        kind: tab === 'apps' ? 'app' : 'gadget',
        registryEntryId: entry.id,
      },
    })
  }

  const openAppFullPage = (entry: RegistryEntry) => {
    // Cap'n Web shared Apps use the same full-viewport viewer as Gadgets.
    if (isCapnWebExtension(entry)) {
      openGadgetFullPage(entry)
      return
    }
    navigate(`/apps/${entry.id}`, {
      state: { name: entry.name, url: resolveExtensionUrl(entry.url), kind: 'app' as const },
    })
  }

  const filteredEntries = useMemo(() => {
    const q = search.trim().toLowerCase()
    const visible = entries.filter((e) => !e.hidden)
    const matches = !q
      ? visible
      : visible.filter((e) => e.name.toLowerCase().includes(q) || e.description.toLowerCase().includes(q))
    return [...matches].sort((a, b) => Number(pinnedIds.has(b.id)) - Number(pinnedIds.has(a.id)))
  }, [entries, search, pinnedIds])

  if (definitionId) {
    return (
      <Box sx={{ maxWidth: 1080, mx: 'auto', px: 4, py: 5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <CircularProgress size={20} />
        <Typography variant="body2">Opening {definitionId}…</Typography>
      </Box>
    )
  }

  const activeMeta = TAB_META[tab]
  const activeColor = theme.palette[activeMeta.colorKey].main

  const showPackageImport = tab === 'gadget' || tab === 'apps'

  return (
    <Box sx={{ maxWidth: 1080, mx: 'auto', px: 4, py: 5 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }} flexWrap="wrap" gap={2}>
        <Typography variant="h3" sx={{ ...SERIF_HEADING, mb: 0 }}>Extensions</Typography>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          {showPackageImport && (
            <>
              <Button
                variant="contained"
                startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
                onClick={() => importInputRef.current?.click()}
                disabled={importing}
              >
                {importing ? 'Importing…' : 'Import'}
              </Button>
              <input
                ref={importInputRef}
                type="file"
                accept=".zip,application/zip"
                hidden
                onChange={handleImportPick}
              />
            </>
          )}
          <TextField
            size="small"
            placeholder="Search…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{ width: 260 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="disabled" />
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      </Stack>

      <Tabs
        value={tab}
        onChange={(_, v) => { setTab(v); setSearch('') }}
        TabIndicatorProps={{ style: { display: 'none' } }}
        sx={{ mb: 2, borderBottom: '1px solid', borderColor: 'divider', minHeight: 44 }}
      >
        {visibleTabs.map(key => {
          const meta = TAB_META[key]
          const active = tab === key
          const color = theme.palette[meta.colorKey].main
          return (
            <Tab
              key={key}
              value={key}
              icon={<meta.icon fontSize="small" />}
              iconPosition="start"
              label={meta.label}
              sx={{
                minHeight: 44,
                textTransform: 'none',
                fontWeight: active ? 600 : 500,
                color: active ? color : 'text.secondary',
                borderBottom: active ? `2px solid ${color}` : '2px solid transparent',
              }}
            />
          )
        })}
      </Tabs>

      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mb: 3, p: 1.25, borderRadius: ds.radiusSm, bgcolor: alpha(activeColor, 0.08) }}
      >
        <InfoOutlinedIcon fontSize="small" sx={{ color: activeColor }} />
        <Typography variant="body2" color="text.secondary">{activeMeta.help}</Typography>
      </Stack>

      {(tab === 'gadget' || tab === 'apps' || tab === 'external') && (
        <Box>
          {loading && <CircularProgress size={22} />}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 1.75 }}>
            {filteredEntries.map(entry => {
              const meta = TAB_META[tab]
              const color = theme.palette[meta.colorKey].main
              const isGadget = tab === 'gadget'
              const isApp = tab === 'apps'
              const capnWeb = isCapnWebExtension(entry)
              const defId = (isGadget || capnWeb) ? gadgetDefinitionId(entry) : null
              const launchUrl = (isGadget || capnWeb)
                ? `${window.location.origin}/extensions/${defId}`
                : resolveExtensionUrl(entry.url)
              const chips = entry.category
                ? ((isGadget || capnWeb) ? entry.category.split(',').map((s) => s.trim()).filter(Boolean) : [entry.category])
                : undefined

              const footerOpen = () => {
                // Product: Open = full viewport. Edit-beside-chat is only "Edit in a chat".
                if (isGadget || (isApp && capnWeb)) openGadgetFullPage(entry)
                else if (isApp) openAppFullPage(entry)
                else window.open(launchUrl, '_blank', 'noopener')
              }

              const menuOpen = footerOpen

              return (
                <ExtensionCard
                  key={entry.id}
                  icon={meta.icon}
                  iconColor={color}
                  iconBg={alpha(color, 0.12)}
                  title={entry.name}
                  subtitle={(isGadget || capnWeb) ? defId ?? undefined : undefined}
                  description={entry.description}
                  chips={chips}
                  meta={entry.updatedAt ? `Updated ${new Date(entry.updatedAt).toLocaleDateString()}` : undefined}
                  actionLabel={meta.actionLabel}
                  actionIcon={meta.actionIcon}
                  onOpen={footerOpen}
                  pinned={pinnedIds.has(entry.id)}
                  menuItems={tab === 'external' ? [
                    { label: meta.actionLabel, icon: meta.actionIcon, onClick: menuOpen },
                    pinnedIds.has(entry.id)
                      ? { label: 'Unpin', icon: PushPinIcon, onClick: () => togglePin(entry), divider: true }
                      : { label: 'Pin', icon: PushPinOutlinedIcon, onClick: () => togglePin(entry), divider: true },
                    { label: 'Delete', icon: DeleteIcon, onClick: () => setDeleteTarget({ id: entry.id, name: entry.name }), danger: true },
                  ] : [
                    { label: meta.actionLabel, icon: meta.actionIcon, onClick: menuOpen },
                    {
                      label: 'Edit in a chat',
                      icon: ForumIcon,
                      onClick: () => ((isGadget || capnWeb) ? openGadgetBesideChat(entry) : editInChat(entry.name)),
                    },
                    {
                      label: 'Share',
                      icon: ShareIcon,
                      onClick: () => openShare(
                        defId ?? entry.id,
                        entry.name,
                      ),
                    },
                    ...(isApp
                      ? [{
                          label: 'Add Users',
                          icon: PersonAddIcon,
                          onClick: () => openAddUsers(
                            defId ?? entry.id,
                            entry.name,
                          ),
                        } satisfies ExtensionCardMenuItem]
                      : []),
                    pinnedIds.has(entry.id)
                      ? { label: 'Unpin', icon: PushPinIcon, onClick: () => togglePin(entry), divider: true }
                      : { label: 'Pin', icon: PushPinOutlinedIcon, onClick: () => togglePin(entry), divider: true },
                    { label: 'Delete', icon: DeleteIcon, onClick: () => setDeleteTarget({ id: entry.id, name: entry.name }), danger: true },
                  ] as ExtensionCardMenuItem[]}
                />
              )
            })}
          </Box>
          {!loading && filteredEntries.length === 0 && !error && (
            <Typography variant="body2" color="text.secondary">
              {entries.length === 0
                ? (tab === 'gadget'
                  ? 'No gadgets registered.'
                  : tab === 'apps'
                    ? 'No apps registered.'
                    : 'No external apps registered.')
                : 'No results match your search.'}
            </Typography>
          )}
        </Box>
      )}

      {tab === 'skills' && showSkills && <SkillsPage />}

      {tab !== 'skills' && (
        <Button sx={{ mt: 3 }} onClick={() => navigate(shell.liveSessionId ? `/chat/${shell.liveSessionId}` : '/chat')}>
          Back to chat
        </Button>
      )}

      <Snackbar
        open={!!snackbar}
        autoHideDuration={2000}
        onClose={() => setSnackbar(null)}
        message={snackbar ?? ''}
      />

      <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ ...SERIF_HEADING, fontSize: '1.05rem' }}>
          Delete {tab === 'gadget' ? 'gadget' : tab === 'apps' ? 'app' : 'external app'}?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.875rem', color: 'text.primary' }}>
            <strong>{deleteTarget?.name}</strong> will be permanently removed. This can&apos;t be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteEntry} disabled={deleting}>
            {deleting ? <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} /> : null}
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <ShareUsersDialog
        open={!!inviteTarget}
        onClose={() => setInviteTarget(null)}
        apiBaseUrl={shell.apiBaseUrl}
        token={shell.token}
        selfEmail={shell.email}
        type={inviteTarget?.type ?? 'share_blueprint'}
        definitionId={inviteTarget?.definitionId ?? ''}
        displayName={inviteTarget?.displayName ?? ''}
        onSent={(count) => {
          setSnackbar(
            inviteTarget?.type === 'add_member'
              ? `Sent ${count} membership invite${count === 1 ? '' : 's'}`
              : `Shared with ${count} user${count === 1 ? '' : 's'}`,
          )
        }}
      />
    </Box>
  )
}
